"use client";

import { useEffect, type ReactNode } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useCurrentUser } from "@/modules/auth/context/CurrentUserContext";
import { getClientRegion, getSupportedRegions, isGlobalRegionSlug } from "@/services/region";
import { rememberRegionChoice } from "@/services/regionCookie";
import { consumeRegionNavigationHandled } from "@/modules/auth/utils/regionNavigationLock";
import { isGlobalOrMultiRegionTeacher } from "@/modules/auth/utils/userAccess";

/**
 * `/{locale}/google-auth/...`, with or without a region prefix.
 */
function isGoogleAuthHandoff(pathname: string): boolean {
  return pathname.split("/").filter(Boolean).includes("google-auth");
}

export default function RegionGuard({ children }: { children: ReactNode }) {
  const { user, loading } = useCurrentUser();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (loading || !user) return;

    // The Google sign-in hand-off is left alone. It is a transient page with a
    // single-use token in its URL and no region-specific content: rewriting
    // that URL remounts it, and the re-run exchange fails because the token is
    // already spent - leaving the user on a spinner that never resolves. The
    // region the account should end up in is applied by the destination this
    // page navigates to, which is the right moment for it.
    if (isGoogleAuthHandoff(pathname)) return;

    // A signed-in account's own region is settled, so proxy.ts must stop
    // geolocating this browser against CF-IPCountry — geolocation is for
    // visitors with no account to ask. Recorded persistently because the
    // proxy's other signal that someone is signed in (the `auth_session`
    // cookie) does not survive closing the browser, while the account does:
    // without this, the first request of every new browser session would be
    // geolocated, and a Global account opening from Egypt would be bounced
    // into /eg before this guard could pull it back out.
    //
    // Written before the early returns below, so it still happens for a Global
    // or multi-region teacher — recorded as "no region" for them, since the
    // reason this guard declines to pin their URL (no single canonical region)
    // is the same reason the proxy must not put one in front of it either.
    const accountRegionSlug = isGlobalOrMultiRegionTeacher(user)
      ? null
      : (user.region as { slug?: string } | undefined)?.slug ?? null;
    rememberRegionChoice(accountRegionSlug);

    // AuthLayout/TeacherLayout/StudentLayout/DashboardLayoutClient already
    // build a region-correct target off the user's actual region when they
    // navigate in reaction to this same `user` change - if one of them just
    // did, this is a parent of all of them, so its effect runs right after
    // theirs in the same commit. Correcting again here would read the stale
    // pathname of the page being navigated away from and race their
    // in-flight navigation.
    if (consumeRegionNavigationHandled()) return;

    // A Global (or multi-region) teacher has no single canonical region to be
    // pinned to — redirecting them to one region's URL would be wrong. Students
    // and single-region teachers keep the existing single-region pinning.
    if (isGlobalOrMultiRegionTeacher(user)) return;

    const regionObj = user.region as { id?: number; slug?: string } | undefined;
    const userRegionSlug = regionObj?.slug;

    // Use window.location on the client side
    const currentPathname = window.location.pathname;
    const currentRegion = getClientRegion(currentPathname);
    const search = window.location.search || "";
    const segments = currentPathname.split("/").filter(Boolean);
    const first = segments[0];
    const supportedRegions = getSupportedRegions();

    // A Global / no-region user has no URL region segment: either no region at
    // all, or THE Global region (regions.type='global'), whose slug is never
    // emitted as a URL segment — its canonical public URL is locale-only
    // (/en, /ar). If they are sitting on a region-prefixed URL (e.g. /eg/en/...),
    // the frontend would otherwise derive "eg" from the URL and treat them as an
    // Egypt user for catalog/pricing. Strip the region prefix so the URL matches
    // their identity (no region). Pinning them to their own slug (as the branch
    // below does for a real geographic region) would produce /global/en, which
    // the proxy can't route — Global is the absence of a segment, not "/global".
    if (!userRegionSlug || isGlobalRegionSlug(userRegionSlug)) {
      if (currentRegion && first && supportedRegions.includes(first.toLowerCase())) {
        const newPathname = "/" + segments.slice(1).join("/");
        router.replace(`${newPathname}${search}`);
      }
      return;
    }

    if (currentRegion?.toLowerCase() !== userRegionSlug.toLowerCase()) {
      let newPathname = currentPathname;

      if (first && supportedRegions.includes(first.toLowerCase())) {
        segments[0] = userRegionSlug.toLowerCase();
        newPathname = "/" + segments.join("/");
      } else {
        newPathname = `/${userRegionSlug.toLowerCase()}${currentPathname}`;
      }

      router.replace(`${newPathname}${search}`);
    }
  }, [user, loading, router, pathname]);

  return <>{children}</>;
}
